import React, { Component } from 'react';
import '../styles/onboarding.css';
import logEvent from '../utils/logger/logEvent';
import { LOG_EVENT_TYPE, LOG_STATUS } from '../utils/logger/eventTypes';
import currentScreenSlug from '../utils/logger/currentScreenSlug';

class OnboardingErrorBoundary extends Component {
  state = { hasError: false, error: null, reported: false };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[OnboardingErrorBoundary]', error, info);
    // Journey logging: this is a system-caught crash, not something the user
    // reported — logged automatically so "where did the journey stop" can
    // include a hard frontend crash, not just API/process failures.
    logEvent(currentScreenSlug(), LOG_EVENT_TYPE.PROCESS_FAILED, {
      action: 'REACT_ERROR_BOUNDARY_CAUGHT',
      status: LOG_STATUS.FAILED,
      errorMessage: error?.message ?? String(error),
      metadata: { componentStack: info?.componentStack ?? null },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.href = '/onboarding';
  };

  // Journey logging: the one explicit "user reported a problem" event in the
  // whole flow, per the plan — deliberately the only new UI added to any
  // existing screen, and only on this fallback boundary screen, never on the
  // normal onboarding steps themselves.
  handleReportIssue = () => {
    logEvent(currentScreenSlug(), LOG_EVENT_TYPE.USER_REPORTED_ERROR, {
      action: 'REPORT_ISSUE_CLICKED',
      status: LOG_STATUS.FAILED,
      errorMessage: this.state.error?.message ?? null,
    });
    this.setState({ reported: true });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="hg-error-boundary"
        >
          <h1 className="hg-error-boundary__title">
            Oops — something went wrong
          </h1>
          <p className="hg-error-boundary__body">
            We hit an unexpected snag. You can try again or head back to the start.
          </p>
          <div className="hg-error-boundary__actions">
            <button
              onClick={this.handleRetry}
              className="hg-error-boundary__button hg-error-boundary__button--primary"
            >
              Try again
            </button>
            <button
              onClick={this.handleGoHome}
              className="hg-error-boundary__button hg-error-boundary__button--secondary"
            >
              Back to start
            </button>
          </div>
          {this.state.reported ? (
            <p className="hg-error-boundary__body">Thanks — we've logged this issue.</p>
          ) : (
            <button
              onClick={this.handleReportIssue}
              className="hg-error-boundary__button hg-error-boundary__button--secondary"
            >
              Report an issue
            </button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default OnboardingErrorBoundary;
